import React, { useState } from 'react';
import { X, Printer, Wifi, Usb, Bluetooth, ChevronRight, ChevronLeft } from 'lucide-react';

/**
 * Add Printer Wizard
 * 
 * Simplified wizard for adding printers without technical jargon.
 * 
 * Steps:
 * 1. Drucker-Art wählen (USB / Bluetooth / Netzwerk)
 * 2. Rolle wählen (Theke/Küche/Bar/A4)
 * 3. Zusammenfassung + Speichern
 */
export const AddPrinterWizard = ({ isOpen, onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [printerData, setPrinterData] = useState({
    name: '',
    connection_type: '',
    role: '',
    enabled: true,
    ip: '',
    port: 9100,
    device_name: ''
  });

  if (!isOpen) return null;

  const handleSave = async () => {
    // Validate
    if (!printerData.name || !printerData.connection_type || !printerData.role) {
      alert('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    // For network printers, IP is recommended but not required (can be configured later)
    if (printerData.connection_type === 'network' && !printerData.ip) {
      const confirm = window.confirm('Keine IP-Adresse angegeben. Drucker kann später konfiguriert werden. Fortfahren?');
      if (!confirm) return;
    }

    // Convert to old format for compatibility
    const printerToSave = {
      id: `printer-${Date.now()}`,
      name: printerData.name,
      type: printerData.connection_type, // network, usb, bluetooth
      role: printerData.role,
      enabled: printerData.enabled,
      ip: printerData.ip || '',
      port: printerData.port || 9100,
      device_name: printerData.device_name || ''
    };

    // Wait for save to complete before closing
    await onSave(printerToSave);
    handleClose();
  };

  const handleClose = () => {
    setPrinterData({
      name: '',
      connection_type: '',
      role: '',
      enabled: true,
      ip: '',
      port: 9100,
      device_name: ''
    });
    setStep(1);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--space-4)',
      }}
      onClick={handleClose}
      data-testid="add-printer-wizard"
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--border-primary)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Printer size={24} style={{ color: 'var(--accent-primary)' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Drucker hinzufügen
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-4) var(--space-6)', background: 'var(--bg-primary)' }}>
          <StepIndicator number={1} label="Drucker-Art" active={step === 1} completed={step > 1} />
          <ChevronRight size={16} style={{ color: 'var(--text-secondary)', alignSelf: 'center' }} />
          <StepIndicator number={2} label="Rolle" active={step === 2} completed={step > 2} />
          <ChevronRight size={16} style={{ color: 'var(--text-secondary)', alignSelf: 'center' }} />
          <StepIndicator number={3} label="Fertig" active={step === 3} completed={false} />
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-6)' }}>
          {step === 1 && (
            <Step1ConnectionType
              printerData={printerData}
              setPrinterData={setPrinterData}
              onNext={() => setStep(2)}
            />
          )}
          
          {step === 2 && (
            <Step2Role
              printerData={printerData}
              setPrinterData={setPrinterData}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          
          {step === 3 && (
            <Step3Summary
              printerData={printerData}
              setPrinterData={setPrinterData}
              onSave={handleSave}
              onBack={() => setStep(2)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Step Indicator Component
const StepIndicator = ({ number, label, active, completed }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 1 }}>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: completed ? 'var(--accent-primary)' : active ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
          color: completed || active ? 'white' : 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: '0.875rem',
        }}
      >
        {completed ? '✓' : number}
      </div>
      <span style={{ fontSize: '0.875rem', color: active ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400 }}>
        {label}
      </span>
    </div>
  );
};

// Step 1: Connection Type
const Step1ConnectionType = ({ printerData, setPrinterData, onNext }) => {
  const connectionTypes = [
    {
      id: 'usb',
      icon: Usb,
      title: 'USB-Drucker (Kassen-PC)',
      description: 'Bon-Drucker per USB-Kabel direkt am PC',
      hint: 'Empfohlen für Theke & Küche',
      recommended: true
    },
    {
      id: 'bluetooth',
      icon: Bluetooth,
      title: 'Bluetooth-Drucker',
      description: 'Drucker per Bluetooth verbinden',
      hint: 'Nur lokal am Kassen-PC oder Tablet'
    },
    {
      id: 'network',
      icon: Wifi,
      title: 'Netzwerk-Drucker (LAN/WLAN)',
      description: 'Drucker ist im lokalen Netzwerk',
      hint: 'Funktioniert von überall im Netzwerk'
    }
  ];

  return (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
        Welche Art Drucker möchten Sie hinzufügen?
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
        Wählen Sie aus, wie der Drucker angeschlossen ist
      </p>

      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        {connectionTypes.map((type) => {
          const Icon = type.icon;
          const isSelected = printerData.connection_type === type.id;

          return (
            <div
              key={type.id}
              onClick={() => setPrinterData({ ...printerData, connection_type: type.id })}
              style={{
                padding: 'var(--space-4)',
                background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-primary)',
                border: `2px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'var(--border-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'var(--border-primary)';
                }
              }}
            >
              {type.recommended && (
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-2)',
                  right: 'var(--space-2)',
                  padding: 'var(--space-1) var(--space-2)',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)'
                }}>
                  Empfohlen
                </div>
              )}
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <div style={{
                  padding: 'var(--space-3)',
                  background: isSelected ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <Icon size={24} style={{ color: isSelected ? 'white' : 'var(--text-primary)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                    {type.title}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                    {type.description}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    {type.hint}
                  </p>
                </div>
                {isSelected && (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'var(--accent-primary)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={onNext}
        disabled={!printerData.connection_type}
        style={{
          marginTop: 'var(--space-6)',
          width: '100%',
          padding: 'var(--space-3) var(--space-6)',
          background: printerData.connection_type ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
          color: printerData.connection_type ? 'white' : 'var(--text-disabled)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: printerData.connection_type ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)'
        }}
      >
        Weiter
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

// Step 2: Role Selection
const Step2Role = ({ printerData, setPrinterData, onNext, onBack }) => {
  const roles = [
    {
      id: 'counter',
      label: 'Theke / Fahrer',
      description: 'Bons & Bewirtungsbelege (Thermodruck, 58mm/80mm)',
      icon: '🧾',
      recommended: true
    },
    {
      id: 'kitchen',
      label: 'Küche',
      description: 'Küchenbon mit Bestellungen (Thermodruck)',
      icon: '👨‍🍳'
    },
    {
      id: 'bar',
      label: 'Bar',
      description: 'Getränke-Bestellungen (Thermodruck)',
      icon: '🍹'
    },
    {
      id: 'invoice_a4',
      label: 'DIN A4 Rechnung',
      description: 'Formale Rechnungen auf A4-Papier (Laserdrucker/Tintenstrahldrucker)',
      icon: '📄'
    }
  ];

  return (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
        Wofür ist dieser Drucker?
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
        Wählen Sie die Rolle des Druckers
      </p>

      {/* Printer Name Input */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
          Drucker-Name *
        </label>
        <input
          type="text"
          value={printerData.name}
          onChange={(e) => setPrinterData({ ...printerData, name: e.target.value })}
          placeholder="z.B. Küchen-Drucker"
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            background: 'var(--bg-primary)',
            border: '2px solid var(--border-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            color: 'var(--text-primary)'
          }}
        />
      </div>

      {/* Role Selection */}
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {roles.map((role) => {
          const isSelected = printerData.role === role.id;

          return (
            <div
              key={role.id}
              onClick={() => setPrinterData({ ...printerData, role: role.id })}
              style={{
                padding: 'var(--space-4)',
                background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-primary)',
                border: `2px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                position: 'relative'
              }}
            >
              {role.recommended && (
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-2)',
                  right: 'var(--space-2)',
                  padding: 'var(--space-1) var(--space-2)',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)'
                }}>
                  Empfohlen
                </div>
              )}
              <div style={{ fontSize: '2rem' }}>{role.icon}</div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                  {role.label}
                </h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                  {role.description}
                </p>
              </div>
              {isSelected && (
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  ✓
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div style={{ marginTop: 'var(--space-6)', display: 'flex', gap: 'var(--space-3)' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-6)',
            background: 'transparent',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)'
          }}
        >
          <ChevronLeft size={20} />
          Zurück
        </button>
        <button
          onClick={() => {
            console.log('Step 2 → 3: name=', printerData.name, 'role=', printerData.role);
            onNext();
          }}
          disabled={!printerData.name || !printerData.role}
          style={{
            flex: 2,
            padding: 'var(--space-3) var(--space-6)',
            background: (printerData.name && printerData.role) ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
            color: (printerData.name && printerData.role) ? 'white' : 'var(--text-disabled)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: (printerData.name && printerData.role) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)'
          }}
        >
          Weiter
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

// Step 3: Summary & Optional Network Config
const Step3Summary = ({ printerData, setPrinterData, onSave, onBack }) => {
  const connectionTypeLabels = {
    usb: 'USB-Drucker (Kassen-PC)',
    bluetooth: 'Bluetooth-Drucker',
    network: 'Netzwerk-Drucker (LAN/WLAN)'
  };

  const roleLabels = {
    counter: 'Theke / Fahrer (Thermodruck, Bewirtungsbelege)',
    kitchen: 'Küche (Thermodruck)',
    bar: 'Bar (Thermodruck)',
    invoice_a4: 'DIN A4 Rechnung (Laserdrucker)'
  };

  return (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
        Zusammenfassung
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--space-6)' }}>
        Prüfen Sie die Angaben und speichern Sie den Drucker
      </p>

      {/* Summary Card */}
      <div style={{
        padding: 'var(--space-5)',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--space-6)'
      }}>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>Name</div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>{printerData.name}</div>
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>Verbindung</div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {connectionTypeLabels[printerData.connection_type]}
          </div>
        </div>

        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>Rolle</div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {roleLabels[printerData.role]}
          </div>
        </div>
      </div>

      {/* USB/Bluetooth Hinweis */}
      {(printerData.connection_type === 'usb' || printerData.connection_type === 'bluetooth') && (
        <div style={{
          padding: 'var(--space-4)',
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-6)',
          fontSize: '0.875rem',
          color: 'var(--text-primary)'
        }}>
          <strong>Hinweis:</strong> {printerData.connection_type === 'usb' ? 'USB' : 'Bluetooth'}-Drucker funktionieren nur direkt am Kassen-PC. 
          {' '}Stellen Sie sicher, dass der Drucker angeschlossen und als Windows-Drucker eingerichtet ist.
        </div>
      )}

      {/* Optional Network Config */}
      {printerData.connection_type === 'network' && (
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-3)' }}>
            Netzwerk-Einstellungen (optional)
          </h4>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
            Sie können diese Einstellungen auch später vornehmen
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                IP-Adresse
              </label>
              <input
                type="text"
                value={printerData.ip}
                onChange={(e) => setPrinterData({ ...printerData, ip: e.target.value })}
                placeholder="z.B. 192.168.1.100"
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                Port
              </label>
              <input
                type="number"
                value={printerData.port}
                onChange={(e) => setPrinterData({ ...printerData, port: parseInt(e.target.value) })}
                placeholder="9100"
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div style={{
        padding: 'var(--space-4)',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-6)',
        fontSize: '0.875rem',
        color: 'var(--text-primary)'
      }}>
        <strong>Hinweis:</strong> Dieser Drucker druckt ab jetzt automatisch alle {roleLabels[printerData.role]}-Belege.
        Sie können die Einstellungen jederzeit ändern.
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-6)',
            background: 'transparent',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)'
          }}
        >
          <ChevronLeft size={20} />
          Zurück
        </button>
        <button
          onClick={onSave}
          style={{
            flex: 2,
            padding: 'var(--space-3) var(--space-6)',
            background: 'var(--accent-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)'
          }}
        >
          Drucker hinzufügen
          <Printer size={20} />
        </button>
      </div>
    </div>
  );
};
